import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { SUBSCRIPTION_SOURCE_MESSAGE_LIMIT } from '../../../../../const';
import { chatIdFromPathname } from '../../../../../service/heldMessage';
import { showMessagePackOffer } from '../../../../../store/slices/chatsSlice';
import { getPaymentModalSource, setShowPaymentModal } from '../../../../../store/slices/modalsSlice';
import { getSubscriptionPlans } from '../../../../../store/slices/paymentsSlice';
import PaymentButton, { paymentBtnType } from '../../../../common/PaymentButton';
import AskWithoutLimitsPaywall from './AskWithoutLimitsPaywall';
import styles from './styles.module.scss';

/**
 * The out-of-messages paywall (Figma 4167-16380). A full-screen takeover rather
 * than the bottom sheet the other paywall states use, so it is rendered outside
 * `Modal` — see PaymentModal.
 *
 * Plan cards are built from the catalogue rather than hardcoded — HORO-10522's
 * monthly/yearly unlimited plans are now live, so this renders two cards,
 * defaulting selection to whichever one carries a discount (yearly).
 *
 * The actual "Ask without limits" screen now lives in AskWithoutLimitsPaywall
 * (shared with TarotLimitModal's registered-user tarot-limit upsell, HORO-
 * 10495) — this component's own job is just supplying the authed-only
 * behavior: closing via Redux, and a CTA that goes straight to checkout via
 * the existing PaymentButton (unchanged fallback-to-`premium` semantics).
 */
const MessageLimitPaywall = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    // Purchasable only: the catalogue also returns a plan the customer already
    // holds once it has been withdrawn from sale (HORO-10539), so that Plan
    // Management can name it. A paywall must never offer one of those as a tile.
    // Ordering and selection live in AskWithoutLimitsPaywall, shared with the
    // tarot-limit upsell.
    const plans = useSelector(getSubscriptionPlans);
    // HORO-10679: this component also backs the advisor-lock and tarot-limit
    // paywalls, so the offer keys off WHY the paywall opened, not off it
    // closing. Read before dispatching — `setShowPaymentModal(false)` clears
    // the source in the same reducer.
    const paymentSource = useSelector(getPaymentModalSource);
    // Not `useChatIdByUrl` — that returns the last path segment whatever it is,
    // so on an advisor landing it hands back the alias and the offer would be
    // keyed to a conversation that doesn't exist yet.
    const chatId = chatIdFromPathname(useLocation().pathname);

    const onClose = () => {
        if (paymentSource === SUBSCRIPTION_SOURCE_MESSAGE_LIMIT && chatId) {
            dispatch(showMessagePackOffer({ chatId }));
        }
        dispatch(setShowPaymentModal(false));
    };

    return (
        <AskWithoutLimitsPaywall
            plans={plans}
            onClose={onClose}
            renderCta={(selected) => (
                <PaymentButton
                    text={t('pages.paywall.continue')}
                    type={paymentBtnType.subscription}
                    plan={selected}
                    extraClasses={[styles.cta]}
                    googleEvent={'chatPopup'}
                />
            )}
        />
    );
};

export default MessageLimitPaywall;
